import { useState, useRef } from "react";
import { X, Download, Printer } from "lucide-react";
import PaymentReceipt from "./PaymentReceipt";
import { StudentWithUserInfo } from "../../types/student";
import toast from "react-hot-toast";

interface ReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: StudentWithUserInfo;
  payment: {
    amount: number;
    paymentMethod: string;
    referenceNumber?: string;
    paymentDate: string;
    notes?: string;
  };
  receiptNumber: string;
  branchInfo?: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
  };
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({
  isOpen,
  onClose,
  student,
  payment,
  receiptNumber,
  branchInfo,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const receiptRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  console.log("ReceiptModal is rendering", {
    isOpen,
    student: student?.userInfo,
    receiptNumber,
  });

  const handleDownloadPDF = async () => {
    if (!payment.referenceNumber) {
      toast.error("Payment reference number is required to download receipt");
      return;
    }

    setIsDownloading(true);
    try {
      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const token = localStorage.getItem("token");

      // Download receipt from backend using the student payment receipt endpoint
      const response = await fetch(
        `${API_URL}/api/students/${student._id}/payment-receipt/${payment.referenceNumber}/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to download receipt: ${response.status}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Receipt-${payment.referenceNumber}.pdf`;
      
      try {
        document.body.appendChild(a);
        a.click();
      } finally {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      toast.success("Receipt downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(`Failed to download receipt: ${errorMessage}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = async () => {
    if (!payment.referenceNumber) {
      toast.error("Payment reference number is required to print receipt");
      return;
    }

    setIsPrinting(true);
    try {
      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const token = localStorage.getItem("token");

      // Download receipt from backend using the student payment receipt endpoint
      const response = await fetch(
        `${API_URL}/api/students/${student._id}/payment-receipt/${payment.referenceNumber}/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to load receipt for printing: ${response.status}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create a URL for the PDF blob
      const pdfUrl = window.URL.createObjectURL(blob);

      // Open PDF in new window/tab for printing
      const printWindow = window.open(pdfUrl, "_blank");

      if (printWindow) {
        // Wait for the PDF to load, then trigger print
        printWindow.onload = () => {
          printWindow.print();
        };

        // Clean up the URL after printing
        printWindow.onbeforeunload = () => {
          window.URL.revokeObjectURL(pdfUrl);
        };

        toast.success("Receipt opened for printing!");
      } else {
        // Fallback: download the PDF if popup is blocked
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `Receipt-${payment.referenceNumber}.pdf`;
        
        try {
          document.body.appendChild(a);
          a.click();
        } finally {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(pdfUrl);
        }

        toast.success("Receipt downloaded. Please open and print the PDF.");
      }
    } catch (error) {
      console.error("Print error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(`Failed to print receipt: ${errorMessage}`);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            Payment Receipt
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Receipt Preview - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div ref={receiptRef} data-receipt-element>
              <PaymentReceipt
                student={student}
                payment={payment}
                receiptNumber={receiptNumber}
                branchInfo={branchInfo}
              />
            </div>
          </div>
        </div>

        {/* Actions - Always visible at bottom */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="mb-3 text-sm text-gray-600 text-center">
            Receipt #{receiptNumber}
          </div>
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="flex items-center px-6 py-3 text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <Printer className="w-5 h-5 mr-2" />
              {isPrinting ? "Printing..." : "Print"}
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <Download className="w-5 h-5 mr-2" />
              {isDownloading ? "Downloading..." : "Download PDF"}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
